import type { CoviActivity, CoviToolCounts, CoviToolStep } from "../model/types"

const ACTIVITIES = [
  "searching",
  "reading",
  "analyzing",
  "proposing",
  "creating",
  "updating",
  "deleting",
  "starting",
  "working",
] as const
const RESOURCE_KINDS = [
  "action",
  "alert",
  "finding",
  "integration",
  "agent",
  "audio_analysis",
  "audio_resource",
  "conversation",
  "dashboard",
  "documentation",
  "image",
  "memory",
  "metric",
  "notebook",
  "persona",
  "playbook",
  "published_skill",
  "report",
  "resource",
  "review_project",
  "run",
  "schedule",
  "schema",
  "test_case",
  "trace",
  "user",
  "visualization",
] as const
const STATES = ["running", "completed", "failed", "reused", "unknown"] as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

/** Allowlist semantic fields; arbitrary resource context never reaches the transcript. */
export function parseToolSteps(value: unknown): CoviToolStep[] | null {
  if (!Array.isArray(value)) {
    return null
  }
  const steps: CoviToolStep[] = []
  for (const entry of value) {
    if (
      !isRecord(entry) ||
      typeof entry.name !== "string" ||
      !entry.name.trim()
    ) {
      continue
    }
    const activity = ACTIVITIES.find(
      (candidate) => candidate === entry.activity,
    )
    const resource_kind = RESOURCE_KINDS.find(
      (candidate) => candidate === entry.resource_kind,
    )
    const state = STATES.find((candidate) => candidate === entry.state)
    steps.push({
      name: entry.name.trim(),
      status: typeof entry.status === "string" ? entry.status : "unknown",
      is_research: entry.is_research !== false,
      is_error: entry.is_error === true,
      ...(activity ? { activity } : {}),
      ...(resource_kind ? { resource_kind } : {}),
      ...(state ? { state } : {}),
    })
  }
  return steps.length ? steps : null
}

const LEGACY_ACTIVITY: Record<string, CoviActivity> = {
  list_alerts: { activity: "searching", resource_kind: "alert" },
  get_alert: { activity: "reading", resource_kind: "alert" },
  list_alert_events: { activity: "reading", resource_kind: "alert" },
  list_alert_baselines: { activity: "searching", resource_kind: "alert" },
  get_alert_baseline: { activity: "reading", resource_kind: "alert" },
  list_alert_scheduled_runs: {
    activity: "searching",
    resource_kind: "schedule",
  },
  get_alert_slack_status: { activity: "reading", resource_kind: "integration" },
  list_investigation_findings: {
    activity: "searching",
    resource_kind: "finding",
  },
  get_investigation_findings: { activity: "reading", resource_kind: "finding" },
  get_investigation_finding_evidence: {
    activity: "reading",
    resource_kind: "finding",
  },
  get_investigation_finding_activity: {
    activity: "reading",
    resource_kind: "finding",
  },
  schedule_this_notebook: { activity: "proposing", resource_kind: "schedule" },
  list_recent_runs: { activity: "searching", resource_kind: "run" },
  get_run: { activity: "reading", resource_kind: "run" },
  get_runs_overview: { activity: "reading", resource_kind: "run" },
  list_run_simulations: { activity: "reading", resource_kind: "run" },
  list_run_simulation_metric_values: {
    activity: "reading",
    resource_kind: "run",
  },
  get_report: { activity: "reading", resource_kind: "report" },
  get_run_report: { activity: "reading", resource_kind: "report" },
  analyze_my_runs: { activity: "analyzing", resource_kind: "run" },
  analyze_run_outliers: { activity: "analyzing", resource_kind: "run" },
  compare_runs: { activity: "analyzing", resource_kind: "run" },
  compare_run_transcripts: { activity: "analyzing", resource_kind: "run" },
  analyze_cohort_outliers: {
    activity: "analyzing",
    resource_kind: "conversation",
  },
  analyze_conversation_cohorts: {
    activity: "analyzing",
    resource_kind: "conversation",
  },
  get_conversation: { activity: "reading", resource_kind: "conversation" },
  get_simulation_output: { activity: "reading", resource_kind: "conversation" },
  get_simulations_bundle: {
    activity: "reading",
    resource_kind: "conversation",
  },
  get_conversations_bundle: {
    activity: "reading",
    resource_kind: "conversation",
  },
  get_simulation_metrics: {
    activity: "reading",
    resource_kind: "conversation",
  },
  list_monitoring_conversations: {
    activity: "searching",
    resource_kind: "conversation",
  },
  scan_monitoring_conversations: {
    activity: "searching",
    resource_kind: "conversation",
  },
  search_uploaded_transcripts: {
    activity: "searching",
    resource_kind: "conversation",
  },
  get_uploaded_conversation_audio: {
    activity: "reading",
    resource_kind: "audio_resource",
  },
  get_conversation_audio_analysis: {
    activity: "reading",
    resource_kind: "audio_analysis",
  },
  analyze_conversation_audio: {
    activity: "starting",
    resource_kind: "audio_analysis",
  },
  get_metric: { activity: "reading", resource_kind: "metric" },
  describe_metric: { activity: "reading", resource_kind: "metric" },
  get_conversation_metrics: { activity: "reading", resource_kind: "metric" },
  get_python_metric_draft: { activity: "reading", resource_kind: "metric" },
  get_python_metric_preview: { activity: "reading", resource_kind: "metric" },
  check_metric_library: { activity: "searching", resource_kind: "metric" },
  get_metric_breakdown: { activity: "analyzing", resource_kind: "metric" },
  get_failure_breakdown: { activity: "analyzing", resource_kind: "metric" },
  analyze_metric_explanations: {
    activity: "analyzing",
    resource_kind: "metric",
  },
  test_sql_metric: { activity: "analyzing", resource_kind: "metric" },
  get_sql_metric_schema: { activity: "reading", resource_kind: "schema" },
  get_agent: { activity: "reading", resource_kind: "agent" },
  list_org_resources: { activity: "searching", resource_kind: "resource" },
  locate_in_app: { activity: "searching", resource_kind: "resource" },
  list_org_users: { activity: "reading", resource_kind: "user" },
  list_test_cases: { activity: "searching", resource_kind: "test_case" },
  get_test_case: { activity: "reading", resource_kind: "test_case" },
  diagnose_failing_test_case: {
    activity: "analyzing",
    resource_kind: "test_case",
  },
  review_test_set_coverage: {
    activity: "analyzing",
    resource_kind: "test_case",
  },
  audit_evaluation_config: {
    activity: "analyzing",
    resource_kind: "test_case",
  },
  get_persona: { activity: "reading", resource_kind: "persona" },
  list_persona_options: { activity: "reading", resource_kind: "persona" },
  analyze_trace_quality: { activity: "analyzing", resource_kind: "trace" },
  inspect_trace_spans: { activity: "analyzing", resource_kind: "trace" },
  get_review_project: { activity: "reading", resource_kind: "review_project" },
  get_review_project_progress: {
    activity: "reading",
    resource_kind: "review_project",
  },
  list_review_annotations: {
    activity: "reading",
    resource_kind: "review_project",
  },
  get_review_project_insights: {
    activity: "analyzing",
    resource_kind: "review_project",
  },
  get_dashboard_data_inventory: {
    activity: "reading",
    resource_kind: "dashboard",
  },
  get_dashboard_row_evidence: {
    activity: "reading",
    resource_kind: "dashboard",
  },
  get_dashboard_rows_by_dimension: {
    activity: "reading",
    resource_kind: "dashboard",
  },
  get_dashboard_transcript_snippets: {
    activity: "reading",
    resource_kind: "dashboard",
  },
  get_dashboard_widgets: { activity: "reading", resource_kind: "dashboard" },
  search_playbooks: { activity: "searching", resource_kind: "playbook" },
  read_playbook: { activity: "reading", resource_kind: "playbook" },
  recall_failure_patterns: { activity: "reading", resource_kind: "playbook" },
  vertical_guidance: { activity: "reading", resource_kind: "playbook" },
  lookup_guidance: { activity: "reading", resource_kind: "playbook" },
  search_docs: { activity: "searching", resource_kind: "documentation" },
  search_published_skills: {
    activity: "searching",
    resource_kind: "published_skill",
  },
  read_published_skill: {
    activity: "reading",
    resource_kind: "published_skill",
  },
  recall_memory: { activity: "reading", resource_kind: "memory" },
  recall_summary_preferences: { activity: "reading", resource_kind: "memory" },
  save_memory: { activity: "creating", resource_kind: "memory" },
  update_memory: { activity: "updating", resource_kind: "memory" },
  save_summary_preferences: { activity: "updating", resource_kind: "memory" },
  delete_memory: { activity: "deleting", resource_kind: "memory" },
  clear_summary_preferences: { activity: "deleting", resource_kind: "memory" },
  propose_org_action: { activity: "proposing", resource_kind: "action" },
  render_visualization: {
    activity: "creating",
    resource_kind: "visualization",
  },
  generate_notebook_image: { activity: "creating", resource_kind: "image" },
  write_notebook: { activity: "proposing", resource_kind: "notebook" },
  update_notebook: { activity: "proposing", resource_kind: "notebook" },
  read_notebook: { activity: "reading", resource_kind: "notebook" },
  read_notebook_evidence: { activity: "reading", resource_kind: "notebook" },
  read_notebook_example: { activity: "reading", resource_kind: "notebook" },
  find_notebook_examples: { activity: "searching", resource_kind: "notebook" },
  get_notebook_schedule_catalog: {
    activity: "reading",
    resource_kind: "schedule",
  },
  create_notebook_schedule: {
    activity: "proposing",
    resource_kind: "schedule",
  },
  read_org_data_result_page: { activity: "reading", resource_kind: "resource" },
}

const RESOURCE_LABELS: Record<
  NonNullable<CoviActivity["resource_kind"]>,
  string
> = {
  action: "an action",
  alert: "alerts",
  finding: "investigation findings",
  integration: "integration status",
  agent: "agent details",
  audio_analysis: "audio analysis",
  audio_resource: "audio download details",
  conversation: "conversations",
  dashboard: "dashboard data",
  documentation: "documentation",
  image: "an image",
  memory: "memory",
  metric: "metrics",
  notebook: "a notebook",
  persona: "personas",
  playbook: "playbooks",
  published_skill: "published skills",
  report: "reports",
  resource: "resources",
  review_project: "human reviews",
  run: "runs",
  schedule: "a schedule",
  schema: "the metric schema",
  test_case: "test cases",
  trace: "traces",
  user: "workspace members",
  visualization: "a chart",
}
const VERBS = {
  searching: ["Searching", "Searched"],
  reading: ["Reading", "Read"],
  analyzing: ["Analyzing", "Analyzed"],
  proposing: ["Preparing", "Prepared"],
  creating: ["Creating", "Created"],
  updating: ["Updating", "Updated"],
  deleting: ["Deleting", "Deleted"],
  starting: ["Starting", "Started"],
  working: ["Working", "Completed a step"],
} as const

interface ToolPresentation extends CoviActivity {
  name: string
  status?: string
  running?: boolean
  isError?: boolean
  is_error?: boolean
  omittedSteps?: CoviToolCounts
  provisional?: boolean
}

export function toolState(
  step: Omit<ToolPresentation, "name">,
): NonNullable<CoviActivity["state"]> {
  if (
    step.isError ||
    step.is_error ||
    ["failed", "unavailable", "invalid_arguments"].includes(step.status ?? "")
  ) {
    return "failed"
  }
  if (step.running || step.status === "started") {
    return "running"
  }
  if (step.state && STATES.includes(step.state)) {
    return step.state
  }
  if (step.status === "succeeded") {
    return "completed"
  }
  if (step.status === "deduped") {
    return "reused"
  }
  return "unknown"
}

export function hasSummaryMemoryChange(steps: CoviToolStep[]): boolean {
  return steps.some(
    (step) =>
      (step.name === "save_summary_preferences" ||
        step.name === "clear_summary_preferences") &&
      ["completed", "reused"].includes(toolState(step)),
  )
}

export function toolLabel(
  name: string,
  step: Omit<ToolPresentation, "name"> = {},
): string {
  const legacy = LEGACY_ACTIVITY[name]
  const activity =
    ACTIVITIES.find((candidate) => candidate === step.activity) ??
    legacy?.activity ??
    "working"
  const resource =
    RESOURCE_KINDS.find((candidate) => candidate === step.resource_kind) ??
    legacy?.resource_kind ??
    "resource"
  const completed = ["completed", "reused"].includes(toolState(step))
  const verb = VERBS[activity][completed ? 1 : 0]
  return activity === "working" ? verb : `${verb} ${RESOURCE_LABELS[resource]}`
}

export function toolStatusLabel(status: string): string | null {
  const labels: Record<string, string> = {
    failed: "Failed",
    unavailable: "Unavailable",
    invalid_arguments: "Invalid arguments",
    deduped: "Reused",
  }
  return Object.hasOwn(labels, status) ? labels[status] : null
}

export function toolTraceCounts(
  steps: Omit<ToolPresentation, "name">[],
): Record<NonNullable<CoviActivity["state"]>, number> {
  const counts = { completed: 0, failed: 0, reused: 0, unknown: 0, running: 0 }
  for (const step of steps) {
    counts[toolState(step)] += 1
    for (const state of STATES) {
      const value = step.omittedSteps?.[state]
      if (
        typeof value === "number" &&
        Number.isSafeInteger(value) &&
        value > 0
      ) {
        counts[state] = Math.min(Number.MAX_SAFE_INTEGER, counts[state] + value)
      }
    }
  }
  return counts
}

export function toolTraceSummary(
  steps: (Omit<ToolPresentation, "name"> & { isResearch?: boolean })[],
): string {
  if (!steps.length) {
    return "What Sofia did"
  }
  const counts = toolTraceCounts(steps)
  if (steps.some((step) => step.provisional) || counts.running) {
    return "Working…"
  }
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0)
  if (counts.completed === total) {
    return `${total} ${total === 1 ? "step" : "steps"} completed`
  }
  return [
    counts.completed ? `${counts.completed} completed` : null,
    counts.reused ? `${counts.reused} reused` : null,
    counts.failed ? `${counts.failed} failed` : null,
    counts.unknown ? `${counts.unknown} outcome unknown` : null,
  ]
    .filter(Boolean)
    .join(" · ")
}
